import {useEffect, useRef, useState, useCallback} from "react";

import {dataManager} from "../managers/DataManager";
import {dataKeys} from "../enums/dataKeys";

function mergeArticlesByIdInOrder(batches) {
  const merged = [];
  const seen = new Set();
  for (const batch of batches) {
    if (!Array.isArray(batch)) continue;
    for (const article of batch) {
      if (article?.id != null && !seen.has(article.id)) {
        seen.add(article.id);
        merged.push(article);
      }
    }
  }
  return merged;
}

export function useArticles() {
  const ref = useRef(null);
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [update, setUpdateArticles] = useState(false);

  const getArticlesByLabelNames = useCallback(async (labelNames) => {
    try {
      console.info(`Searching for articles with labels:\n${labelNames.split(',').map((label) => `  - ${label}`).join('\n')}`);
      const response = await fetch(`/api/zendesk/articles?label_names=${encodeURIComponent(labelNames)}`);
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const result = await response.json();
      setArticles(result);
    } catch (error) {
      console.error(`Could not fetch articles for labels="${labelNames}". ${error}`);
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getArticlesByLabelOrGroups = useCallback(async (labelOrGroups) => {
    try {
      const results = await Promise.all(
        labelOrGroups.map(async (group) => {
          const labelNames = [...new Set(group)].join(',');
          console.info(`Searching for articles with labels (OR branch):\n${labelNames.split(',').map((label) => `  - ${label}`).join('\n')}`);
          try {
            const response = await fetch(`/api/zendesk/articles?label_names=${encodeURIComponent(labelNames)}`);
            if (!response.ok) {
              throw new Error(response.statusText);
            }
            return await response.json();
          } catch (error) {
            console.error(`Could not fetch articles for labels="${labelNames}". ${error}`);
            return [];
          }
        })
      );
      setArticles(mergeArticlesByIdInOrder(results));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchGroups = dataManager.getArticleLabelFetchGroups();
    const labels = dataManager.getFlatArrayOfUniqueValuesByKey(dataKeys.labels);
    const labelNames = labels.join(',');
    const hasFetchGroups = Array.isArray(fetchGroups) && fetchGroups.length > 0;
    const hasLabels = Boolean(labelNames);

    if (!hasFetchGroups && !hasLabels) {
      setArticles([]);
      setIsLoading(false);
      return;
    }

    if ((update || !ref.current) && (hasFetchGroups || hasLabels)) {
      ref.current = true;
      setIsLoading(true);
      if (hasFetchGroups) {
        getArticlesByLabelOrGroups(fetchGroups);
      } else {
        getArticlesByLabelNames(labelNames);
      }
    }
  }, [update, getArticlesByLabelNames, getArticlesByLabelOrGroups])

  return [articles, isLoading, setUpdateArticles]
}
